import { useMemo, useState } from "react";
import { api, brl } from "../../lib/api";
import { processarFoto, logErroUpload } from "../../lib/image";
import { Camera, CheckCircle2, Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import Counter from "./Counter";

const SERVICO_LABEL = { montagem: "Montagem de móveis", desmontagem: "Desmontagem de móveis", embalagem: "Embalagem de móveis e objetos" };
const SERVICO_LABEL_SHORT = { montagem: "Montagem", desmontagem: "Desmontagem", embalagem: "Embalagem" };

export default function ServicosAdicionaisStep({ cfg, value, onChange, fotos, onFotosChange }) {
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);

  const sel = value || { itens: {}, pacote_id: "" };
  const pacoteAtivo = (cfg?.pacotes || []).find((p) => p.id === sel.pacote_id) || null;
  const servicosNoPacote = new Set(pacoteAtivo?.servicos || []);

  const setQtd = (srv, itemId, q) => {
    const itens = { ...(sel.itens || {}) };
    itens[srv] = { ...(itens[srv] || {}), [itemId]: q };
    onChange({ ...sel, itens });
  };

  const setPacote = (id) => onChange({ ...sel, pacote_id: sel.pacote_id === id ? "" : id });

  const total = useMemo(() => {
    let t = 0;
    if (pacoteAtivo) t += pacoteAtivo.preco;
    Object.entries(cfg?.servicos || {}).forEach(([srv, s]) => {
      if (servicosNoPacote.has(srv)) return;
      (s.itens || []).forEach((item) => {
        t += (sel.itens?.[srv]?.[item.id] || 0) * item.preco;
      });
    });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, sel]);

  const qtdEmServico = (srv) => Object.values(sel.itens?.[srv] || {}).reduce((a, b) => a + b, 0);
  const envolveMontDesmont =
    servicosNoPacote.has("montagem") || servicosNoPacote.has("desmontagem") ||
    qtdEmServico("montagem") > 0 || qtdEmServico("desmontagem") > 0;
  const algoSelecionado = pacoteAtivo || ["montagem", "desmontagem", "embalagem"].some((k) => qtdEmServico(k) > 0);

  const upload = async (fileList) => {
    const files = [...fileList];
    if (!files.length) return;
    setUploading(true);
    try {
      const otimizadas = [];
      for (const f of files) otimizadas.push(await processarFoto(f));
      const fd = new FormData();
      otimizadas.forEach((f) => fd.append("files", f));
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const novas = r.data.files.map((f, i) => ({ path: f.path, preview: URL.createObjectURL(otimizadas[i]) }));
      setPreviews((p) => [...p, ...novas]);
      onFotosChange([...(fotos || []), ...r.data.files.map((x) => x.path)]);
      toast.success("Fotos enviadas!");
    } catch (e) {
      files.forEach((f) => logErroUpload("servicos_adicionais", e, f));
      toast.error(e.response?.data?.detail || "Não foi possível enviar a foto. Tente novamente.");
    } finally {
      setUploading(false);
    }
  };

  const chips = (fotos || []).map((path) => previews.find((p) => p.path === path) || { path, preview: "" });

  return (
    <div className="space-y-4" data-testid="servicos-adicionais-step">
      <h2 className="font-heading text-2xl font-bold text-navy">Serviços adicionais</h2>
      <p className="text-sm text-slate-500">
        Escolha montagem, desmontagem ou embalagem informando a quantidade de cada item — ou selecione um pacote com preço fechado.
      </p>

      {(cfg?.pacotes || []).length > 0 && (
        <div className="space-y-2" data-testid="serv-add-pacotes">
          <p className="text-xs font-bold tracking-wider uppercase text-slate-400">Pacotes com preço fechado</p>
          {(cfg?.pacotes || []).map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`serv-add-pacote-${p.id}`}
              onClick={() => setPacote(p.id)}
              className={`w-full p-4 rounded-2xl border-2 text-left active:scale-[0.98] transition-all flex items-center gap-3 ${
                sel.pacote_id === p.id ? "border-[#F5B301] bg-gold-light" : "border-slate-200 bg-white"
              }`}
            >
              <Package className="w-6 h-6 text-navy shrink-0" />
              <div className="flex-1">
                <p className="font-heading font-bold text-navy text-sm">{p.nome}</p>
                <p className="text-[11px] text-slate-500">
                  Inclui: {(p.servicos || []).map((s) => SERVICO_LABEL_SHORT[s] || s).join(" + ")}
                </p>
              </div>
              <p className="font-heading font-extrabold text-navy text-sm">{brl(p.preco)}</p>
            </button>
          ))}
          {pacoteAtivo && (
            <p className="text-[11px] text-amber-700 font-semibold" data-testid="serv-add-pacote-info">
              Pacote selecionado: os serviços incluídos nele não cobram itens individuais. Toque novamente para remover.
            </p>
          )}
        </div>
      )}

      {Object.entries(cfg?.servicos || {}).map(([srv, s]) => {
        const noPacote = servicosNoPacote.has(srv);
        return (
          <div key={srv} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`serv-add-${srv}`}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold text-navy text-sm uppercase">{SERVICO_LABEL[srv] || srv}</h3>
              {noPacote && (
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`serv-add-${srv}-no-pacote`}>
                  INCLUÍDO NO PACOTE
                </span>
              )}
            </div>
            {noPacote ? (
              <p className="text-[11px] text-slate-400">Este serviço já está no pacote selecionado — sem cobrança adicional.</p>
            ) : (
              <div className="space-y-2 mt-2">
                {(s.itens || []).map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-2" data-testid={`serv-add-item-${item.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-navy truncate">{item.nome}</p>
                      <p className="text-[11px] text-slate-400">{brl(item.preco)} por item</p>
                    </div>
                    <Counter
                      testId={`serv-add-qtd-${item.id}`}
                      value={sel.itens?.[srv]?.[item.id] || 0}
                      onChange={(q) => setQtd(srv, item.id, q)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {algoSelecionado && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3" data-testid="serv-add-fotos-card">
          <h3 className="font-heading font-bold text-navy text-sm uppercase">
            Fotos dos itens {envolveMontDesmont ? <span className="text-red-500">*</span> : <span className="text-slate-400 font-normal">(opcional)</span>}
          </h3>
          <p className="text-[11px] text-slate-500">
            {envolveMontDesmont
              ? "Envie fotos dos móveis que serão montados ou desmontados — obrigatório para confirmar o serviço."
              : "Se quiser, envie fotos dos itens a embalar."}
          </p>
          <label
            data-testid="serv-add-upload-label"
            className="flex flex-col items-center justify-center gap-2 w-full py-8 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 text-slate-500 cursor-pointer active:scale-[0.98] transition-transform"
          >
            {uploading ? <Loader2 className="w-7 h-7 animate-spin text-[#F5B301]" /> : <Camera className="w-7 h-7 text-navy" />}
            <span className="text-xs font-semibold">{uploading ? "Enviando..." : "Toque para enviar fotos"}</span>
            <input
              data-testid="serv-add-upload-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => upload(e.target.files)}
            />
          </label>
          {chips.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="serv-add-fotos-grid">
              {chips.map((p, i) =>
                p.preview ? (
                  <img key={i} src={p.preview} alt="" className="w-full h-24 object-cover rounded-xl border border-slate-200" />
                ) : (
                  <div key={i} className="w-full h-24 rounded-xl border border-emerald-200 bg-emerald-50 flex flex-col items-center justify-center gap-1">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="text-[9px] font-bold text-emerald-700">Foto enviada</span>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}

      {algoSelecionado && (
        <div className="flex justify-between items-center bg-navy rounded-2xl px-4 py-3.5" data-testid="serv-add-total">
          <span className="text-xs font-bold text-slate-300 uppercase tracking-wide">Total dos serviços adicionais</span>
          <span className="font-heading font-extrabold text-[#F5B301]">{brl(total)}</span>
        </div>
      )}
    </div>
  );
}
